// client/src/components/services_tester/RoutesModal.jsx
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, Button, Chip, Paper, TextField,
  List, ListItem, IconButton, Alert,
  CircularProgress, Tab, Tabs, Accordion, AccordionSummary, AccordionDetails,
  Tooltip, Snackbar,
} from '@mui/material';
import {
  Close, Code, Search, ContentCopy,
  ExpandMore, Refresh, Route, Public, Lan, Cached,
} from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';

// ✅ Используем SERVICE_URLS (уже через window.ENV / HTTPS)
import {
  DIRECT_SERVICES,
  SERVICE_URLS,
  API_ENDPOINTS,
} from '../../store/constants/servicesTesterConstants';

// ============================================================================
// Маппинг сервисов → базовый URL (SERVICE_URLS — уже через window.ENV)
// ============================================================================

const getServiceUrl = (serviceKey) => {
  switch (serviceKey) {
    case 'universe_gateway':
      return SERVICE_URLS.UNIVERSE_GATEWAY;
    case 'imcon':
      return SERVICE_URLS.IM_CON;
    case 'world_manager':
      return SERVICE_URLS.WORLD_MANAGER;
    case 'sim_character':
      return SERVICE_URLS.SIM_CHARACTER;
    case 'character_designer':
      return SERVICE_URLS.CHARACTER_DESIGNER;
    default:
      return null;
  }
};

// ============================================================================
// Вспомогательные компоненты
// ============================================================================

const ServiceTabs = ({ services, activeService, onServiceChange }) => {
  const safeServices = services || [];
  return (
    <Tabs
      value={activeService}
      onChange={(_, v) => onServiceChange(v)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      {safeServices.map((service) => (
        <Tab
          key={service.key}
          label={service.name}
          value={service.key}
          icon={<Code />}
          iconPosition="start"
          sx={{ textTransform: 'none' }}
        />
      ))}
    </Tabs>
  );
};

const RouteItem = ({ route, onCopy }) => {
  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'success';
      case 'POST': return 'primary';
      case 'PUT': return 'warning';
      case 'DELETE': return 'error';
      case 'PATCH': return 'info';
      default: return 'default';
    }
  };

  if (!route) return null;

  return (
    <ListItem sx={{ p: 0, mb: 0.5 }}>
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 0.75,
          borderLeft: 3,
          borderColor: `${getMethodColor(route.method)}.main`,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Chip
            label={route.method || 'GET'}
            color={getMethodColor(route.method)}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', minWidth: 50 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                wordBreak: 'break-all',
                display: 'block',
                fontSize: '0.7rem',
              }}
            >
              {route.path || 'No path'}
            </Typography>
            {route.controller && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontSize: '0.6rem' }}
              >
                🎯 {route.controller.replace('Elixir.', '')}
              </Typography>
            )}
            {route.action && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontSize: '0.6rem' }}
              >
                ⚡ {route.action}
              </Typography>
            )}
          </Box>
          <Tooltip title="Копировать путь">
            <IconButton
              size="small"
              onClick={() => onCopy(route.path || '')}
              sx={{ alignSelf: 'flex-start', p: 0.25 }}
            >
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    </ListItem>
  );
};

// ============================================================================
// Диагностика сервиса
// ============================================================================

const ServiceDiagnostics = ({ service }) => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [checking, setChecking] = useState(false);

  if (!service) {
    return <Alert severity="warning">Информация о сервисе недоступна</Alert>;
  }

  const runDiagnostics = async () => {
    setChecking(true);
    try {
      const baseUrl = getServiceUrl(service.key);
      if (!baseUrl) {
        throw new Error(`URL для сервиса ${service.key} не найден в window.ENV`);
      }

      const endpoints = [
        { name: 'API Debug Routes', url: `${baseUrl}/api/debug/routes` },
        { name: 'API Health Check', url: `${baseUrl}/api/health` },
        { name: 'Base API Path', url: `${baseUrl}/api/` },
      ];

      const results = [];

      for (const endpoint of endpoints) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);

          const startTime = Date.now();
          const response = await fetch(endpoint.url, {
            method: 'GET',
            signal: controller.signal,
            headers: { Accept: 'application/json' },
          });
          clearTimeout(timeoutId);

          const responseTime = Date.now() - startTime;
          const contentType = response.headers.get('content-type') || '';
          let data = null;

          try {
            if (contentType.includes('application/json')) {
              data = await response.json();
            } else {
              const raw = await response.text();
              data = {
                _type: raw.startsWith('<!DOCTYPE') ? 'html' : 'text',
                preview: raw.substring(0, 150),
                length: raw.length,
              };
            }
          } catch (parseError) {
            data = { _error: 'Parse error: ' + parseError.message };
          }

          results.push({
            ...endpoint,
            status: response.status,
            statusText: response.statusText,
            responseTime,
            ok: response.ok,
            contentType,
            data,
          });
        } catch (error) {
          results.push({
            ...endpoint,
            error: error.name === 'AbortError' ? 'Timeout (8s)' : error.message,
            ok: false,
          });
        }
      }

      setDiagnostics(results);
    } catch (error) {
      setDiagnostics([{ name: 'Diagnostics Error', error: error.message, ok: false }]);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Lan sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
            Диагностика {service.name}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Cached fontSize="small" />}
              onClick={runDiagnostics}
              disabled={checking}
              sx={{ fontSize: '0.7rem', py: 0.25 }}
            >
              {checking ? 'Диагностика…' : 'Запустить диагностику'}
            </Button>
          </Box>

          {diagnostics && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {diagnostics.map((result, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 0.75, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Chip
                      label={result.ok ? 'OK' : 'FAIL'}
                      color={result.ok ? 'success' : 'error'}
                      size="small"
                      sx={{ height: 16, fontSize: '0.55rem' }}
                    />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.65rem', wordBreak: 'break-all' }}>
                      {result.url}
                    </Typography>
                    {result.responseTime && (
                      <Chip label={`${result.responseTime}ms`} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
                    )}
                  </Box>
                  {result.error && (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.6rem', display: 'block' }}>
                      ❌ {result.error}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ============================================================================
// Основной компонент
// ============================================================================

export default function RoutesModal({ open, onClose, services }) {
  const safeServices = services || DIRECT_SERVICES;
  const defaultService = safeServices.length > 0 ? safeServices[0].key : '';

  const [activeService, setActiveService] = useState(defaultService);
  const [routes, setRoutes] = useState({});
  const [loading, setLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const loadRoutes = async (serviceKey) => {
    setLoading((prev) => ({ ...prev, [serviceKey]: true }));

    try {
      const baseUrl = getServiceUrl(serviceKey);
      if (!baseUrl) {
        throw new Error(`URL для сервиса ${serviceKey} не найден в window.ENV`);
      }

      // ✅ URL через SERVICE_URLS (HTTPS), не http://localhost:PORT
      const debugUrl = `${baseUrl}/api/debug/routes`;
      console.log(`🔄 Loading API routes from: ${debugUrl}`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(debugUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`API эндпоинт /api/debug/routes не найден (404)`);
        }
        throw new Error(`HTTP ${response.status} - ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const routesData = await response.json();
        const list = Array.isArray(routesData) ? routesData : routesData.routes || [];
        setRoutes((prev) => ({
          ...prev,
          [serviceKey]: {
            data: list,
            source: 'api',
            loadedAt: new Date().toISOString(),
            count: list.length,
          },
        }));
        showSnackbar(`Загружено ${list.length} роутов для ${serviceKey}`);
      } else {
        const errorText = await response.text();
        throw new Error(`Сервер вернул не JSON (${contentType}): ${errorText.substring(0, 200)}`);
      }
    } catch (error) {
      console.error(`❌ Failed to load API routes for ${serviceKey}:`, error);
      setRoutes((prev) => ({
        ...prev,
        [serviceKey]: {
          error: error.message,
          data: [],
          source: 'error',
          count: 0,
        },
      }));
      showSnackbar(`Ошибка загрузки для ${serviceKey}: ${error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, [serviceKey]: false }));
    }
  };

  const handleServiceChange = (serviceKey) => {
    setActiveService(serviceKey);
    if (!routes[serviceKey] && !loading[serviceKey]) {
      loadRoutes(serviceKey);
    }
  };

  const handleCopyPath = async (path) => {
    try {
      await navigator.clipboard.writeText(path);
      showSnackbar(`API путь скопирован: ${path}`);
    } catch (error) {
      showSnackbar('Ошибка копирования пути');
    }
  };

  const currentServiceData = routes[activeService] || {};
  const currentRoutes = currentServiceData.data || [];
  const hasError = currentServiceData.error;
  const isLoading = loading[activeService];

  const filteredRoutes = currentRoutes.filter((route) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (route.path && route.path.toLowerCase().includes(s)) ||
      (route.method && route.method.toLowerCase().includes(s)) ||
      (route.controller && route.controller.toLowerCase().includes(s)) ||
      (route.action && route.action.toLowerCase().includes(s))
    );
  });

  const currentService = safeServices.find((s) => s.key === activeService);

  if (safeServices.length === 0) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>API Routes Debug</DialogTitle>
        <DialogContent>
          <Alert severity="warning">Нет доступных сервисов</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '95vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 1,
            py: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
              🗺️ API Routes Debug
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Просмотр всех API эндпоинтов через /api/debug/routes
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, pt: 1 }}>
            <ServiceTabs
              services={safeServices}
              activeService={activeService}
              onServiceChange={handleServiceChange}
            />
          </Box>

          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              placeholder="Поиск по путям, методам, контроллерам…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 200, '& input': { fontSize: '0.75rem' } }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />,
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<Refresh fontSize="small" />}
              onClick={() => loadRoutes(activeService)}
              disabled={isLoading}
              sx={{ fontSize: '0.7rem', py: 0.25 }}
            >
              {isLoading ? <CircularProgress size={14} color="inherit" /> : 'Routes'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Public fontSize="small" />}
              onClick={() => {
                safeServices.forEach((service) => {
                  if (!routes[service.key] && !loading[service.key]) {
                    loadRoutes(service.key);
                  }
                });
              }}
              disabled={Object.values(loading).some(Boolean)}
              sx={{ fontSize: '0.7rem', py: 0.25 }}
            >
              Все
            </Button>
          </Box>

          {currentService && (
            <Box sx={{ px: 1.5, py: 1 }}>
              <ServiceDiagnostics service={currentService} />
            </Box>
          )}

          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
            {currentService && (
              <Alert
                severity={hasError ? 'error' : currentRoutes.length > 0 ? 'success' : 'info'}
                sx={{ mb: 1, py: 0.25, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0 } }}
                icon={<Route sx={{ fontSize: 16 }} />}
                action={
                  currentServiceData.source && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => loadRoutes(activeService)}
                      startIcon={<Refresh fontSize="small" />}
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Обновить
                    </Button>
                  )
                }
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                  <strong>{currentService.name}</strong> | Найдено: <strong>{filteredRoutes.length}</strong>
                  {isLoading && ' | ⏳'}
                </Typography>
                {hasError && (
                  <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem', display: 'block' }}>
                    ❌ {hasError}
                  </Typography>
                )}
              </Alert>
            )}

            {filteredRoutes.length > 0 ? (
              <List sx={{ p: 0 }}>
                {filteredRoutes.map((route, index) => (
                  <RouteItem key={index} route={route} onCopy={handleCopyPath} />
                ))}
              </List>
            ) : !isLoading && currentServiceData.source === 'api' && (
              <Alert severity="warning" sx={{ fontSize: '0.7rem' }}>
                API роуты загружены, но не найдено эндпоинтов по поиску.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 1, gap: 1 }}>
          <Button onClick={onClose} size="small" startIcon={<Close fontSize="small" />} sx={{ fontSize: '0.7rem' }}>
            Закрыть
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {Object.values(routes).filter((r) => r.source === 'api').length} / {safeServices.length} сервисов
          </Typography>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="info"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
